import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, CircularProgress, Alert, Button, Typography, Chip } from '@mui/material';
import useApi from '../hooks/useApi';
import ProductSelectionsTable from './ProductSelectionsTable';
import SendHistoryResponseModal from './SendHistoryResponseModal';
import ConfirmDialog from './ConfirmDialog';
import { buildSendHistoryRows, SEND_HISTORY_ADDITIONAL_COLUMNS } from './SendHistory';
import { useEnrichedSendHistoryRows } from '../hooks/useEnrichedSendHistoryRows';

const DealerSendHistoryTab = ({ bac }) => {
  const { useVmtSendHistory, useDeleteVmtSendHistory } = useApi();
  const { data, isLoading, isError, error, refetch, isFetching } = useVmtSendHistory({ bac, limit: 200 });
  const deleteHistory = useDeleteVmtSendHistory();
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [confirmState, setConfirmState] = useState({
    open: false,
    ids: [],
    description: '',
  });

  const rawRows = useMemo(() => buildSendHistoryRows(data), [data]);
  const { rows, meta } = useEnrichedSendHistoryRows(rawRows);
  useEffect(() => {
    setSelectionModel((prev) => prev.filter((id) => rows.some((row) => row.id === id)));
  }, [rows]);
  useEffect(() => {
    setSelectionModel((prev) => prev.filter((id) => rows.some((row) => row.id === id)));
  }, [rows]);

  const handleRowClick = useCallback((params) => {
    if (!params?.row?.__sendEntry) return;
    const { __sendEntry, ...rest } = params.row;
    setSelectedRow({
      entry: __sendEntry,
      record: rest,
    });
  }, []);

  const getEntryIdsFromRowIds = useCallback(
    (ids) => {
      const idSet = new Set();
      ids.forEach((rowId) => {
        const row = rows.find((item) => item.id === rowId);
        const entryId = row?.__sendEntry?.id;
        if (entryId) {
          idSet.add(entryId);
        }
      });
      return Array.from(idSet);
    },
    [rows]
  );

  const handleDeleteSelected = useCallback(() => {
    const entryIds = getEntryIdsFromRowIds(selectionModel);
    if (entryIds.length === 0) return;
    setConfirmState({
      open: true,
      ids: entryIds,
      description:
        entryIds.length === 1
          ? 'Delete this history entry?'
          : `Delete ${entryIds.length} history entries?`,
    });
  }, [selectionModel, getEntryIdsFromRowIds]);

  const handleClearAll = useCallback(() => {
    const allEntryIds = Array.from(
      new Set(rawRows.map((row) => row?.__sendEntry?.id).filter((id) => Boolean(id)))
    );
    if (allEntryIds.length === 0) return;
    setConfirmState({
      open: true,
      ids: allEntryIds,
      description: 'Clear all history entries for this dealer?',
    });
  }, [rawRows]);

  const closeConfirmDialog = useCallback(() => {
    if (deleteHistory.isPending) return;
    setConfirmState({ open: false, ids: [], description: '' });
  }, [deleteHistory.isPending]);

  const confirmDeletion = useCallback(async () => {
    if (confirmState.ids.length === 0) return;
    try {
      await deleteHistory.mutateAsync(confirmState.ids);
      setSelectionModel([]);
      await refetch();
    } catch (mutationError) {
      console.error('Failed to delete dealer history entries', mutationError);
    } finally {
      setConfirmState({ open: false, ids: [], description: '' });
    }
  }, [confirmState.ids, deleteHistory, refetch]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || 'Failed to load send history.'}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minHeight: 0,
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Send History</Typography>
        <Button variant="outlined" size="small" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>

      {rows.length === 0 ? (
        <Alert severity="info">No send history records found for this dealer.</Alert>
      ) : (
        <>
          {meta.usedCache && (
            <Chip label="Pulled from cache" color="warning" size="small" sx={{ alignSelf: 'flex-start' }} />
          )}
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={selectionModel.length === 0 || deleteHistory.isPending}
              onClick={handleDeleteSelected}
            >
              Delete Selected
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={rows.length === 0 || deleteHistory.isPending}
              onClick={handleClearAll}
            >
              Clear Dealer History
            </Button>
          </Box>
          <ProductSelectionsTable
            rows={rows}
            additionalColumns={SEND_HISTORY_ADDITIONAL_COLUMNS}
            additionalColumnsPlacement="start"
            onRowClick={handleRowClick}
            selectionModel={selectionModel}
            onSelectionModelChange={setSelectionModel}
          />
        </>
      )}

      <SendHistoryResponseModal
        open={Boolean(selectedRow)}
        onClose={() => setSelectedRow(null)}
        entry={selectedRow?.entry || null}
        record={selectedRow?.record || null}
      />
      <ConfirmDialog
        open={confirmState.open}
        title="Confirm Deletion"
        description={confirmState.description}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteHistory.isPending}
        onConfirm={confirmDeletion}
        onCancel={closeConfirmDialog}
      />
    </Box>
  );
};

export default DealerSendHistoryTab;
